/**
 * BidDeskPage
 * -----------
 * Agency solicitation intake → AskIsaac scope extraction → internal scoping
 * → branded Sterile Mate Subcontractor RFQ packets per trade.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, Loader2, FileText, Download, RefreshCw, Trash2, Mail, Copy,
  Gavel, CheckCircle2, AlertTriangle, ChevronLeft, Building2, NotebookPen, BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API, useAuth } from "@/App";
import BidSourcingPanel from "@/components/admin/BidSourcingPanel";
import BidNotesIntake from "@/components/admin/BidNotesIntake";
import BidNotesBrief from "@/components/admin/BidNotesBrief";
import EquipmentPriceBook from "@/components/admin/EquipmentPriceBook";
import StandardsCompass from "@/components/admin/StandardsCompass";
import SiteSurveyPanel from "@/components/admin/SiteSurveyPanel";
import BidAwardPanel from "@/components/admin/BidAwardPanel";
import TrainingLauncher from "@/components/training/TrainingLauncher";
import QuoteDeskPanel from "@/components/admin/QuoteDeskPanel";
import ProgramRollupPanel from "@/components/admin/ProgramRollupPanel";

const MODELS = [
  { value: "multi_trade", label: "Multi-Trade Package — one RFQ per discipline" },
  { value: "turnkey_gc", label: "Single Turnkey GC — one sub prices everything" },
];

const STATUS_PILL = {
  parsing: "bg-amber-50 text-amber-700 ring-amber-300",
  parsed: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  failed: "bg-red-50 text-red-700 ring-red-300",
};

const Field = ({ label, value, onChange, type = "text" }) => (
  <div>
    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</label>
    <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} />
  </div>
);

const Stat = ({ label, value, accent = "text-slate-900" }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
    <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p className={`text-xl font-black mt-1 ${accent}`}>{value}</p>
  </div>
);

export default function BidDeskPage() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [sol, setSol] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showBook, setShowBook] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bid-desk/solicitations`, { headers });
      setRows(r.data?.solicitations || []);
      setSummary(r.data?.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the bid desk");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const openSol = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/bid-desk/solicitations/${id}`, { headers });
      setSol(r.data.solicitation);
      setOpenId(id);
    } catch (e) {
      toast.error("Could not open the solicitation");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Notes-based opportunities live on the same board but have their own detail
  // view (engineering brief + calc sheet + draft response letter).
  const openNote = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/bid-desk/notes/${id}`, { headers });
      setSol(r.data.note);
      setOpenId(id);
    } catch {
      toast.error("Could not open that opportunity");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openRow = useCallback((row) => (
    row.source === "notes" ? openNote(row.id) : openSol(row.id)
  ), [openNote, openSol]);

  // poll while AskIsaac is reading the packet
  useEffect(() => {
    if (!openId || sol?.parse_status !== "parsing") return;
    const t = setInterval(() => openSol(openId), 5000);
    return () => clearInterval(t);
  }, [openId, sol?.parse_status, openSol]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/bid-desk/solicitations`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Packet uploaded — AskIsaac is reading it now", {
        description: "Scope extraction usually takes 30-60 seconds",
      });
      await load();
      openSol(r.data.solicitation.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const patch = (path, value) => setSol((s) => {
    const next = { ...s };
    if (path.length === 1) next[path[0]] = value;
    else next[path[0]] = { ...(s[path[0]] || {}), [path[1]]: value };
    return next;
  });

  const patchParsed = (key, value) => setSol((s) => ({
    ...s, parsed: { ...(s.parsed || {}), [key]: value },
  }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/bid-desk/solicitations/${sol.id}`, {
        parsed: sol.parsed, sourcing_model: sol.sourcing_model,
        selected_trades: sol.selected_trades, milestones: sol.milestones,
        flowdowns: sol.flowdowns, questionnaire: sol.questionnaire,
        markup_pct: Number(sol.markup_pct || 0), internal_notes: sol.internal_notes,
        status: sol.status,
      }, { headers });
      toast.success("Bid scope saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reparse = async () => {
    try {
      await axios.post(`${API}/bid-desk/solicitations/${sol.id}/reparse`, {}, { headers });
      toast.info("Re-reading the packet…");
      setSol((s) => ({ ...s, parse_status: "parsing" }));
    } catch (e) {
      toast.error("Could not re-parse");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.title || row.parsed?.solicitation_number || row.source_file?.name}?`)) return;
    try {
      await axios.delete(`${API}/bid-desk/solicitations/${row.id}`, { headers });
      toast.success("Deleted"); setOpenId(null); setSol(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const rfqPdf = async (trade) => {
    try {
      const r = await axios.get(
        `${API}/bid-desk/solicitations/${sol.id}/rfq/pdf?trade=${encodeURIComponent(trade)}`,
        { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `SterileMate-RFQ-${trade}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not build the RFQ");
    }
  };

  const copyEmail = async (trade) => {
    try {
      const r = await axios.get(
        `${API}/bid-desk/solicitations/${sol.id}/rfq/email-draft?trade=${encodeURIComponent(trade)}`,
        { headers });
      await navigator.clipboard.writeText(`${r.data.subject}\n\n${r.data.body}`);
      toast.success("Sourcing letter copied", { description: "Paste into Outlook and attach the RFQ PDF" });
    } catch {
      toast.error("Could not build the sourcing letter");
    }
  };

  const trades = (sol?.parsed?.trades || []).map((t) => t.label).filter(Boolean);
  const selected = sol?.selected_trades || [];
  const toggleTrade = (label) => patch(["selected_trades"],
    selected.includes(label) ? selected.filter((t) => t !== label) : [...selected, label]);

  // ------------------------------------------------------------------ list
  if (!openId) {
    return (
      <div className="space-y-5" data-testid="bid-desk-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-black text-slate-900 inline-flex items-center gap-2">
              <Gavel className="w-6 h-6 text-blue-700" /> Bid Desk
            </h1>
            <p className="text-sm text-slate-500">
              Drop in a METRO, SAM.gov, Harris Health or VHA packet — AskIsaac pulls the scope apart and
              builds your subcontractor RFQs.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <TrainingLauncher trackId="bid-desk" />
          <Button size="sm" variant="outline" className="h-10"
            onClick={() => setShowBook(true)} data-testid="bid-price-book-btn">
            <BookOpen className="w-4 h-4 mr-1.5" /> Price book
          </Button>
          <button onClick={() => setShowNotes(true)}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700"
            data-testid="bid-notes-open-btn">
            <NotebookPen className="w-4 h-4" /> Build quote from notes
          </button>
          <label className={`inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-semibold text-white cursor-pointer ${uploading ? "bg-slate-400" : "bg-blue-600 hover:bg-blue-700"}`}
            data-testid="bid-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Upload bid packet"}
            <input type="file" accept="application/pdf" className="hidden" disabled={uploading}
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="bid-upload-input" />
          </label>
          </div>
        </div>

        <ProgramRollupPanel token={token} />
        <SiteSurveyPanel scope="all" linkType="standalone" token={token} label="the Bid Desk" />
        <QuoteDeskPanel token={token} />

        <BidNotesIntake open={showNotes} onClose={() => setShowNotes(false)} token={token}
          onCreated={(n) => { load(); openNote(n.id); }} />
        <EquipmentPriceBook open={showBook} onClose={() => setShowBook(false)} token={token} />

        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Solicitations" value={summary.total} />
            <Stat label="Being read" value={summary.parsing} accent="text-amber-600" />
            <Stat label="Ready to scope" value={summary.ready} accent="text-emerald-600" />
            <Stat label="Awarded" value={summary.awarded} accent="text-blue-700" />
          </div>
        )}

        {loading ? (
          <p className="text-slate-500 inline-flex items-center gap-2 py-8">
            <Loader2 className="w-4 h-4 animate-spin" />Loading…</p>
        ) : rows.length === 0 ? (
          <label className="block text-center py-16 rounded-xl border-2 border-dashed border-slate-300 bg-white cursor-pointer hover:border-blue-400"
            data-testid="bid-desk-empty">
            <Upload className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-semibold">Drop your first bid packet here</p>
            <p className="text-sm text-slate-400 mt-1">PDF up to 60 MB — 200-page solicitations are fine</p>
            <input type="file" accept="application/pdf" className="hidden"
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto shadow-sm">
            <table className="w-full min-w-[900px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Solicitation", "Agency", "Trades", "Agency bid due", "Sub bids due to us", "Scope", ""].map((h) => (
                    <th key={h} className="text-left px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/70 cursor-pointer"
                    onClick={() => openRow(row)} data-testid={`bid-row-${row.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900">
                        {row.title || row.parsed?.title || row.source_file?.name}
                      </p>
                      <p className="text-xs text-slate-500 font-mono inline-flex items-center gap-1.5">
                        {row.source === "notes" ? (
                          <span className="inline-flex items-center gap-1 font-sans text-[9px] font-black uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">
                            <NotebookPen className="w-2.5 h-2.5" /> Notes
                          </span>
                        ) : null}
                        {row.parsed?.solicitation_number || (row.source === "notes" ? `${(row.attachments || []).length} attachments` : "—")}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {row.parsed?.agency || row.customer || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {row.source === "notes"
                        ? `${(row.open_questions || []).length} questions`
                        : (row.parsed?.trades || []).length || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {row.parsed?.submission_deadline || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-blue-700">
                      {row.milestones?.sub_quote_due || <span className="text-slate-300 font-normal">not set</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${STATUS_PILL[row.parse_status] || STATUS_PILL.parsing}`}>
                        {row.parse_status === "parsing" && <Loader2 className="w-3 h-3 animate-spin" />}
                        {row.parse_status === "parsed" && <CheckCircle2 className="w-3 h-3" />}
                        {row.parse_status === "failed" && <AlertTriangle className="w-3 h-3" />}
                        {row.parse_status}
                      </span>
                      {row.converted_project_id ? (
                        <span className="block mt-1 text-[9px] font-black uppercase tracking-wide text-white bg-emerald-600 rounded-full px-2 py-0.5 w-fit"
                          data-testid={`bid-row-converted-${row.id}`}>
                          Converted → {row.converted_project_id}
                        </span>
                      ) : row.status === "awarded" ? (
                        <span className="block mt-1 text-[9px] font-black uppercase tracking-wide text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 w-fit">
                          Won — convert to project
                        </span>
                      ) : row.status === "lost" ? (
                        <span className="block mt-1 text-[9px] font-black uppercase tracking-wide text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 w-fit">
                          Lost
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); remove(row); }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------- detail
  if (!sol) return <p className="text-slate-500 py-8">Loading…</p>;
  if (sol.source === "notes") {
    return (
      <BidNotesBrief
        note={sol}
        token={token}
        onBack={() => { setOpenId(null); setSol(null); load(); }}
        onRefresh={load}
      />
    );
  }
  const p = sol.parsed || {};

  return (
    <div className="space-y-5" data-testid="bid-desk-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2">
          <Button size="sm" variant="ghost" onClick={() => { setOpenId(null); setSol(null); load(); }}
            data-testid="bid-back-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-black text-slate-900">{p.title || sol.source_file?.name}</h1>
            <p className="text-sm text-slate-500">
              {p.agency || "Agency"} · <span className="font-mono">{p.solicitation_number || "—"}</span>
              {p.contract_term ? ` · ${p.contract_term}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}${sol.source_file?.url}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
            <FileText className="w-4 h-4" /> Agency packet
          </a>
          <Button size="sm" variant="outline" onClick={reparse} data-testid="bid-reparse-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Re-read
          </Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving}
            data-testid="bid-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Save scope
          </Button>
        </div>
      </div>

      {sol.parse_status === "parsing" && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 inline-flex items-center gap-2 w-full"
          data-testid="bid-parsing-banner">
          <Loader2 className="w-4 h-4 animate-spin" />
          AskIsaac is reading the packet — deadlines, trades, bonding, wage and badging will populate here.
        </div>
      )}
      {sol.parse_status === "failed" && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="bid-parse-error">
          Scope extraction failed: {sol.parse_error}. Try <strong>Re-read</strong>, or fill the fields in by hand.
        </div>
      )}

      {p.executive_summary && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">AskIsaac summary</p>
          <p className="text-sm text-slate-700 leading-relaxed">{p.executive_summary}</p>
        </div>
      )}

      {/* Deadline bar — theirs vs ours */}
      <div className="grid sm:grid-cols-3 gap-3" data-testid="bid-deadline-bar">
        <div className="rounded-xl border border-slate-300 bg-slate-900 text-white p-4">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Prime bid due to agency</p>
          <p className="text-base font-black mt-1">{p.submission_deadline || "—"}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">{p.agency || "Agency"}</p>
        </div>
        <div className="rounded-xl border-2 border-blue-500 bg-blue-50 p-4">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-blue-700">
            Our internal deadline — all sub bids in
          </p>
          <Input className="mt-1 bg-white font-bold" value={sol.milestones?.sub_quote_due || ""}
            placeholder="e.g. Fri Sep 4, 2026 · 12:00 PM CST"
            onChange={(e) => patch(["milestones", "sub_quote_due"], e.target.value)}
            data-testid="internal-bid-due-input" />
          <p className="text-[11px] text-blue-800/70 mt-1">
            Printed on every subcontractor RFQ and alerted a week out
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">RFI cutoff</p>
          <p className="text-base font-black mt-1 text-slate-900">{p.rfi_cutoff || "—"}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Site walk: {sol.milestones?.site_walk || p.prebid_meeting?.date || "TBD"}
          </p>
        </div>
      </div>

      {/* Key dates + agency facts */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Solicitation facts — edit anything AskIsaac misread</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Solicitation no." value={p.solicitation_number} onChange={(v) => patchParsed("solicitation_number", v)} />
          <Field label="Issuing agency" value={p.agency} onChange={(v) => patchParsed("agency", v)} />
          <Field label="Place of performance" value={p.facility_address} onChange={(v) => patchParsed("facility_address", v)} />
          <Field label="Prime bid due" value={p.submission_deadline} onChange={(v) => patchParsed("submission_deadline", v)} />
          <Field label="RFI cutoff" value={p.rfi_cutoff} onChange={(v) => patchParsed("rfi_cutoff", v)} />
          <Field label="Contract term" value={p.contract_term} onChange={(v) => patchParsed("contract_term", v)} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-xs text-slate-600">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <p className="font-black uppercase tracking-wider text-[10px] text-slate-500 mb-1">Bonding &amp; wage</p>
            <p>{p.bonding?.notes || p.bonding?.performance_bond || "—"}</p>
            <p className="mt-1">{p.prevailing_wage?.act ? `${p.prevailing_wage.act}. ${p.prevailing_wage.notes || ""}` : "No wage determination found"}</p>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <p className="font-black uppercase tracking-wider text-[10px] text-slate-500 mb-1">Site access &amp; payment</p>
            <p>{[p.site_access?.safety_course, p.site_access?.badging, p.site_access?.notes].filter(Boolean).join(" · ") || "—"}</p>
            <p className="mt-1">
              Retainage {p.payment_terms?.retainage_pct || "—"} · {p.payment_terms?.progress_payments || "—"}
              {p.payment_terms?.notes ? ` · ${p.payment_terms.notes}` : ""}
            </p>
          </div>
        </div>
        {(p.submittal_checklist || []).length > 0 && (
          <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
              Agency submittal checklist ({p.submittal_checklist.length})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {p.submittal_checklist.map((s, i) => (
                <span key={i} className="text-[11px] bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">{s}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Scoping wizard */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Internal bid scoping</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">How are we sourcing it?</label>
            <select value={sol.sourcing_model} onChange={(e) => patch(["sourcing_model"], e.target.value)}
              className="w-full border border-slate-300 rounded-md h-9 px-2 text-sm" data-testid="bid-model-select">
              {MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <Field label="Our markup %" type="number" value={sol.markup_pct} onChange={(v) => patch(["markup_pct"], v)} />
        </div>

        <div className="grid sm:grid-cols-4 gap-3">
          {[["Site walk", "site_walk"], ["Sub quote due", "sub_quote_due"],
            ["Mobilization", "mobilization"], ["Completion target", "completion"]].map(([label, key]) => (
            <Field key={key} label={label} value={sol.milestones?.[key]}
              onChange={(v) => patch(["milestones", key], v)} />
          ))}
        </div>

        <div>
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Flow down to subcontractors</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-700">
            {[["bonding_required", "Bonding"], ["davis_bacon", "Davis-Bacon wages"],
              ["badging_required", "Badging / safety course"], ["immunization_required", "Immunization records"],
              ["rail_safety_certified", "Rail safety certified"]].map(([key, label]) => (
              <label key={key} className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={!!sol.flowdowns?.[key]}
                  onChange={(e) => patch(["flowdowns", key], e.target.checked)}
                  data-testid={`bid-flowdown-${key}`} />
                {label}
              </label>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            <Field label="Insurance — each occurrence" value={sol.flowdowns?.insurance_each_occurrence}
              onChange={(v) => patch(["flowdowns", "insurance_each_occurrence"], v)} />
            <Field label="Insurance — aggregate" value={sol.flowdowns?.insurance_aggregate}
              onChange={(v) => patch(["flowdowns", "insurance_aggregate"], v)} />
          </div>
        </div>

        <div className="grid sm:grid-cols-5 gap-3">
          {["who", "what", "when", "where", "how"].map((k) => (
            <div key={k}>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">{k}</label>
              <Textarea rows={2} value={sol.questionnaire?.[k] || ""}
                onChange={(e) => patch(["questionnaire", k], e.target.value)}
                data-testid={`bid-q-${k}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Trade packages */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Trade packages</p>
            <p className="text-sm text-slate-500">
              Tick the trades we are sourcing, then pull the branded RFQ and the sourcing letter for each one.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => patch(["selected_trades"], trades)}
              data-testid="bid-select-all-trades">Select all</Button>
            <Button size="sm" variant="outline" onClick={() => patch(["selected_trades"], [])}>Clear</Button>
          </div>
        </div>

        {trades.length === 0 ? (
          <p className="text-sm text-slate-400">
            No trades extracted yet{sol.parse_status === "parsing" ? " — AskIsaac is still reading" : ""}.
          </p>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {(sol.parsed.trades || []).map((t, i) => {
              const on = selected.includes(t.label);
              return (
                <div key={i} className={`rounded-lg border p-3 ${on ? "border-blue-300 bg-blue-50/40" : "border-slate-200"}`}
                  data-testid={`bid-trade-${t.label}`}>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={on} onChange={() => toggleTrade(t.label)}
                      className="mt-1" data-testid={`bid-trade-check-${t.label}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />{t.label}
                        {t.naics ? <span className="text-[10px] font-mono text-slate-400">NAICS {t.naics}</span> : null}
                      </p>
                      <p className="text-xs text-slate-500 line-clamp-3 mt-0.5">{t.scope_summary}</p>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {(t.key_tasks || []).length} priced line items
                        {t.license_required ? ` · ${t.license_required}` : ""}
                      </p>
                    </div>
                  </label>
                  <div className="flex items-center gap-2 mt-2.5">
                    <Button size="sm" variant="outline" onClick={() => rfqPdf(t.label)}
                      data-testid={`bid-rfq-pdf-${t.label}`}>
                      <Download className="w-3.5 h-3.5 mr-1.5" />RFQ PDF
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyEmail(t.label)}
                      data-testid={`bid-rfq-email-${t.label}`}>
                      <Copy className="w-3.5 h-3.5 mr-1.5" />Sourcing letter
                    </Button>
                    <a href={`${API}/bid-desk/solicitations/${sol.id}/rfq/preview?trade=${encodeURIComponent(t.label)}`}
                      target="_blank" rel="noreferrer"
                      className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" />Preview
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <StandardsCompass linkType="solicitation" linkId={sol.id} token={token}
        label={sol.title || sol.parsed?.solicitation_number || "Solicitation"} />

      <SiteSurveyPanel linkType="bid_record" linkId={sol.id} token={token}
        label={sol.title || sol.parsed?.title || "this solicitation"} />

      <BidAwardPanel record={sol} token={token} onChanged={() => openSol(sol.id)} />

      <BidSourcingPanel sol={sol} token={token}
        onConverted={(pid) => { openSol(sol.id); window.open(`/admin/projects/${pid}`, "_blank"); }} />

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Internal notes</label>
        <Textarea rows={3} value={sol.internal_notes || ""}
          onChange={(e) => patch(["internal_notes"], e.target.value)} data-testid="bid-notes" />
      </div>
    </div>
  );
}
